import { Component, inject, ViewChild } from '@angular/core';
import { NgForm, UntypedFormControl } from '@angular/forms';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { UtilFns } from 'app/shared/util-fns';
import _ from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IdentityRole, RoleSelection, UserWithRoles } from './identity-data';
import { UserAccountsService } from './user-accounts.service';


export interface UserAccountDialogParams extends DialogParams {
  user?: UserWithRoles;
  users?: UserWithRoles[];
  roles?: IdentityRole[];
  userName?: string;
  isNew?: boolean;
}

@Component({
  templateUrl: './user-account-dialog.component.html',
  standalone: false
})
export class UserAccountDialogComponent extends DialogBaseComponent<UserAccountDialogParams> {
  @ViewChild('userForm') userForm: NgForm;

  params: UserAccountDialogParams;
  user: UserWithRoles;
  users: UserWithRoles[];
  roles: IdentityRole[];
  userRoles: RoleSelection[];
  confirmPassword: string;
  isUserAdmin: boolean;
  pwRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})');

  // to make it available in template
  UtilFns = UtilFns;

  // Use inject() to avoid constructor signature conflict with base class
  private userAccountsService = inject(UserAccountsService);

  constructor(
    public config: DynamicDialogConfig<UserAccountDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    this.params = this.config.data;

    this.validateConfirm = this.validateConfirm.bind(this);
    this.validateInitials = this.validateInitials.bind(this);
    this.validateEmail = this.validateEmail.bind(this);

    this.isUserAdmin = this.authUser.isUserAdmin;

    if (this.params.users) {
      this.setUsers(this.params.user, this.params.users, this.params.roles);
    } else {
      this.userAccountsService.getIdentityData().then(idata => {
        const user = idata.Users.find(u => u.User.UserName === this.params.userName);
        this.setUsers(user, idata.Users, idata.Roles);
      });
    }

    Object.assign(this.config, {
      header: "User Account Edit",
      width: '900px',
      height: '600px'
    });

    this.isPageReady = true;
  }

  hasError(x: any) {
    return x.errors !== null;
  }

  setUsers(user: UserWithRoles, users: UserWithRoles[], roles: IdentityRole[]) {
    this.user = user;
    this.confirmPassword = this.user.Password;
    this.users = users;
    this.roles = roles;
    this.userRoles = this.roles.map(r => {
      return {
        name: r.Name,
        isSelected: this.user.Roles.includes(r.Name)
      };
    });
    this.userRoles = _.sortBy(this.userRoles, x => x.name);
  }

  canEditInitials() {
    return this.params.isNew;
  }

  canSave() {
    const form = this.userForm;
    var ok = form && !form.form.pristine && !form.form.invalid;
    return ok;
  }

  async save() {
    const form = this.userForm;
    const user = this.user;
    if (!user || !form || !form.valid) {
      return;
    }
    if (user.Password != null && user.Password !== this.confirmPassword) {
      return;
    }
    if (user.User.UserName.indexOf('@') > 0) {
      this.user.User.Email = this.user.User.UserName;
    }
    this.user.Roles = this.userRoles.filter(r => r.isSelected).map(r => r.name);
    const uwr = await this.userAccountsService.saveUser(this.user);

    // If the user saved their own profile, refresh the cached auth user
    const currentUser = this.authService.getUser();
    if (uwr && currentUser && currentUser.loginName === uwr.User.UserName) {
      await this.authService.refreshUser();
    }

    this.dialogRef.close(uwr);
  }

  close() {
    this.dialogRef.close(null);
  }

  /** Validation errors are exposed by the UniFieldComponent */
  validateConfirm(ctl: UntypedFormControl) {
    if (this.user == null || this.user.Password == null) { return null; }

    const isValid = ctl.value === this.user.Password;
    if (isValid) {
      return null;
    } else {
      return {
        confirm: {
          message: 'Passwords do not match'
        }
      };
    }
  }

  validateInitials(ctl: UntypedFormControl) {
    if (!ctl.value || !this.user || !this.users) { return null; }
    const isValid = !this.users.some(u => u.Initials === ctl.value && u.id !== this.user.id);
    if (isValid) {
      return null;
    } else {
      return {
        initials: {
          message: 'Initials already in use'
        }
      };
    }
  }

  validateEmail(ctl: UntypedFormControl) {
    if (!ctl.value || !this.user || !this.users) { return null; }
    const isValid = !this.users.some(u => u.User.Email?.toLowerCase() === ctl.value?.toLowerCase() && u.id !== this.user.id);
    if (isValid) {
      return null;
    } else {
      return {
        email: {
          message: 'Email already in use'
        }
      };
    }
  }

}
